import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, AlertTriangle } from "lucide-react";
import { getRateioEmpresas, putRateioEmpresa, getPrejuizoFiscalSaldo, putPrejuizoFiscalSaldo, getRoleFromToken } from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { EMPRESAS_DRE, CATEGORIAS_RATEIO_EMPRESAS } from "../../constants/dreEmpresa";
import { thStyle, tdStyle } from "../../components/gestaoOrcamentaria/DreGerencialView";

// Premissas · Rateio entre Empresas: extraído de DreEmpresaView.js (era o "Painel de Rateio
// entre Empresas" dentro de DRE por Empresa) — aqui é a tela dedicada, sempre carregada aberta.
// DRE por Empresa continua consumindo esses percentuais/saldos (ver services/dre_empresa_svc.py),
// só a edição saiu de lá.

const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  success: "#10B981",
  danger: "#EF4444",
});

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);

const inputPctStyle = (C) => ({
  width: 64, padding: "5px 6px", borderRadius: 6, fontSize: 12.5, textAlign: "right",
  border: `1px solid ${C.border}`, background: C.bg, color: C.text,
});

export default function PremissasRateio() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();
  const isAdmin = getRoleFromToken() === "admin";

  const [ano, setAno] = useState(ANO_ATUAL+1);
  const [percentuais, setPercentuais] = useState({});
  const [saldoPrejuizo, setSaldoPrejuizo] = useState({});
  const [salvando, setSalvando] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    setErro("");
    Promise.all([getRateioEmpresas(ano), getPrejuizoFiscalSaldo(ano)])
      .then(([rateio, prejuizo]) => {
        if (cancelado) return;
        const mapa = {};
        for (const c of rateio.categorias) mapa[c.categoria] = { ...c.percentuais };
        setPercentuais(mapa);
        const mapaPrejuizo = {};
        for (const p of prejuizo) mapaPrejuizo[p.empresa] = p.saldo_inicial;
        setSaldoPrejuizo(mapaPrejuizo);
      })
      .catch((e) => !cancelado && setErro(e.message))
      .finally(() => !cancelado && setCarregando(false));
    return () => { cancelado = true; };
  }, [ano]);

  function somaCategoria(categoria) {
    const valores = percentuais[categoria] || {};
    return EMPRESAS_DRE.reduce((acc, emp) => acc + (Number(valores[emp]) || 0), 0);
  }

  function atualizarPercentual(categoria, empresa, valor) {
    setPercentuais((prev) => ({ ...prev, [categoria]: { ...prev[categoria], [empresa]: valor } }));
  }

  async function salvarCategoria(categoria) {
    const soma = somaCategoria(categoria);
    if (Math.abs(soma - 100) > 0.01) return;
    setSalvando(categoria);
    setErro("");
    try {
      await putRateioEmpresa({
        categoria, ano_referencia: ano,
        percentuais: Object.fromEntries(EMPRESAS_DRE.map((emp) => [emp, Number(percentuais[categoria]?.[emp]) || 0])),
      });
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(null);
    }
  }

  async function salvarSaldoPrejuizo(empresa) {
    setSalvando(`prejuizo-${empresa}`);
    setErro("");
    try {
      await putPrejuizoFiscalSaldo({ empresa, ano_referencia: ano, saldo_inicial: Number(saldoPrejuizo[empresa]) || 0 });
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(null);
    }
  }

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)",
          color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Premissas · Rateio entre Empresas</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select
              value={ano}
              onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
            }}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />}
              {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button
              onClick={() => navigate("/inicio")}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 14px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              Menu Principal
            </button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          {erro && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`,
              border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13,
            }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.text }}>Premissas · Rateio entre Empresas</h1>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              Faturamento Bruto (Backlog) e Impostos não entram aqui — o primeiro já é apurado por empresa,
              o segundo é calculado a partir do LAIR de cada empresa. As 7 categorias abaixo ainda não são
              apuradas por empresa nos módulos de origem — o percentual é uma aproximação; a soma entre
              GT/CIGTA/CRIA precisa ser exatamente 100% para salvar. Investimentos e Despesas Financeiras
              estão aqui como tratamento assumido, a confirmar. Usado por "DRE por Empresa" (Resultado).
              {!isAdmin && " Somente admin edita — os demais perfis visualizam."}
            </p>
          </div>

          {carregando ? (
            <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>
          ) : (
            <>
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={thStyle(C, { textAlign: "left" })}>Categoria</th>
                        {EMPRESAS_DRE.map((emp) => <th key={emp} style={thStyle(C)}>{emp}</th>)}
                        <th style={thStyle(C)}>Soma</th>
                        <th style={thStyle(C)}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {CATEGORIAS_RATEIO_EMPRESAS.map(({ chave, label }) => {
                        const soma = somaCategoria(chave);
                        const valido = Math.abs(soma - 100) < 0.01;
                        return (
                          <tr key={chave}>
                            <td style={tdStyle(C, { textAlign: "left" })}>{label}</td>
                            {EMPRESAS_DRE.map((emp) => (
                              <td key={emp} style={tdStyle(C)}>
                                <input
                                  type="number" step="0.01"
                                  value={percentuais[chave]?.[emp] ?? ""}
                                  onChange={(e) => atualizarPercentual(chave, emp, e.target.value)}
                                  disabled={!isAdmin}
                                  style={{ ...inputPctStyle(C), opacity: isAdmin ? 1 : 0.6 }}
                                />
                              </td>
                            ))}
                            <td style={tdStyle(C, { fontWeight: 700, color: valido ? C.success : C.danger })}>
                              {soma.toFixed(2)}%
                            </td>
                            <td style={tdStyle(C)}>
                              {isAdmin && (
                                <button
                                  type="button" disabled={!valido || salvando === chave}
                                  onClick={() => salvarCategoria(chave)}
                                  style={{
                                    background: "transparent", border: `1px solid ${C.brand}`, borderRadius: 6,
                                    padding: "4px 10px", fontSize: 11, fontWeight: 600, color: C.brand,
                                    cursor: valido ? "pointer" : "not-allowed", opacity: valido ? 1 : 0.4,
                                  }}
                                >
                                  {salvando === chave ? "Salvando…" : "Salvar"}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8 }}>
                  Saldo Inicial de Prejuízo Fiscal ({ano})
                </div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  {EMPRESAS_DRE.map((emp) => (
                    <div key={emp} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: C.muted, minWidth: 44 }}>{emp}</span>
                      <input
                        type="number" step="0.01"
                        value={saldoPrejuizo[emp] ?? ""}
                        onChange={(e) => setSaldoPrejuizo((prev) => ({ ...prev, [emp]: e.target.value }))}
                        disabled={!isAdmin}
                        style={{ width: 130, padding: "5px 6px", borderRadius: 6, fontSize: 12.5, textAlign: "right", border: `1px solid ${C.border}`, background: C.bg, color: C.text, opacity: isAdmin ? 1 : 0.6 }}
                      />
                      {isAdmin && (
                        <button
                          type="button" disabled={salvando === `prejuizo-${emp}`}
                          onClick={() => salvarSaldoPrejuizo(emp)}
                          style={{
                            background: "transparent", border: `1px solid ${C.brand}`, borderRadius: 6,
                            padding: "4px 10px", fontSize: 11, fontWeight: 600, color: C.brand, cursor: "pointer",
                          }}
                        >
                          {salvando === `prejuizo-${emp}` ? "Salvando…" : "Salvar"}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
